/**
 * Pieces shared by more than one buying screen. Nothing here is general enough
 * for `src/ui/` — it all knows about POs, vendors or invoice parsing.
 */

import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Check, Search } from 'lucide-react';
import { api } from '@/lib/api/client';
import type { PaginatedResponse, ProductSummary, Vendor } from '@/lib/api/types';
import { Badge, type Tone } from '@/ui/Badge';
import { Input, Select } from '@/ui/Field';
import { Skeleton } from '@/ui/Panel';
import { cn } from '@/lib/cn';
import { humanize, money, num } from '@/lib/format';
import type { ImportStatus, MatchConfidence, ParserWarning, PoStatus } from './types';
import { vendorKeys } from './api';

/* ── Status chips ─────────────────────────────────────────────────────────────
   One tone map per lifecycle, imported everywhere, so an ordered PO is never
   amber on one screen and grey on another.                                   */

const PO_TONE: Record<string, Tone> = {
  draft: 'neutral',
  submitted: 'info',
  partially_received: 'warn',
  received: 'positive',
  cancelled: 'negative',
};

/**
 * What a PO status is called on screen. The stored value stays `submitted`
 * (the API, the floor phone and the Svelte pages all use it); on the console it
 * reads "Ordered", which is what the Mark as ordered button says it did.
 */
export function poStatusLabel(status: string): string {
  return status === 'submitted' ? 'Ordered' : humanize(status);
}

/**
 * TASK-0251 (INC-4): an order closed short is stored as received; it reads "Closed short" so nobody takes
 * it for complete.
 */
export function PoStatusBadge({
  status,
  closedShort = false,
}: {
  status: string;
  closedShort?: boolean;
}) {
  if (closedShort && status === 'received') {
    return (
      <Badge tone="warn" dot>
        Closed short
      </Badge>
    );
  }
  return (
    <Badge tone={PO_TONE[status] ?? 'neutral'} dot>
      {poStatusLabel(status)}
    </Badge>
  );
}

export function poStatusTone(status: string): Tone {
  return PO_TONE[status] ?? 'neutral';
}

/** `matched` is the API's word; operators read it as "ready to review". */
const IMPORT_TONE: Record<string, Tone> = {
  queued: 'neutral',
  parsing: 'info',
  matched: 'signal',
  failed: 'negative',
  confirmed: 'positive',
};

const IMPORT_LABEL: Record<string, string> = {
  queued: 'Queued',
  parsing: 'Parsing',
  matched: 'Ready to review',
  failed: 'Failed',
  confirmed: 'Imported',
};

export function ImportStatusBadge({ status }: { status: string }) {
  const parsing = status === 'parsing' || status === 'queued';
  return (
    <Badge tone={IMPORT_TONE[status] ?? 'neutral'} dot pulse={parsing}>
      {IMPORT_LABEL[status] ?? humanize(status)}
    </Badge>
  );
}

export function importStatusLabel(status: string): string {
  return IMPORT_LABEL[status] ?? humanize(status);
}

const CONFIDENCE_TONE: Record<MatchConfidence, Tone> = {
  high: 'positive',
  medium: 'warn',
  low: 'negative',
  none: 'negative',
};

export function ConfidenceBadge({
  confidence,
  method,
}: {
  confidence: MatchConfidence;
  method?: string;
}) {
  return (
    <Badge tone={CONFIDENCE_TONE[confidence] ?? 'neutral'}>
      {confidence === 'none' ? 'No match' : `${humanize(confidence)} match`}
      {method && method !== 'none' ? ` · ${method.replace(/_/g, ' ')}` : ''}
    </Badge>
  );
}

/* ── Parser warnings ─────────────────────────────────────────────────────────
   The parser emits closed-enum codes with structured meta; prose is only a
   fallback for a code this build does not know yet.                          */

export function describeWarning(warning: ParserWarning): string {
  const meta = (warning.meta ?? {}) as Record<string, unknown>;
  switch (warning.code) {
    case 'pre_priced':
      return `Pre-priced item (read from the ${String(meta.source ?? 'invoice')})`;
    case 'unit_cost_derived':
      return `Unit cost derived from the case price ÷ ${num(Number(meta.divisor ?? 0))}`;
    case 'oos_zeroed':
      return `Out of stock on the invoice ("${String(meta.original ?? '')}") — received as 0`;
    case 'missing_units_per_case':
      return 'Invoice did not state units per case — case mode unavailable';
    case 'base_net_delta':
      return `Base ${money(Number(meta.baseCents ?? 0))} vs net ${money(
        Number(meta.netCents ?? 0)
      )} — check which one is billed`;
    case 'ambiguous_unit':
      return `Parser could not tell ea from lb (saw ${
        Array.isArray(meta.candidates) ? meta.candidates.join(', ') : 'no unit'
      }) — set the unit before confirming`;
    case 'weight_no_avg':
      return `Weight-billed line with no average case weight on file (${num(
        Number(meta.weightLbs ?? 0)
      )} lb over ${num(Number(meta.cases ?? 0))} cases)`;
    default:
      return warning.message ?? humanize(warning.code);
  }
}

export function WarningList({ warnings }: { warnings: ParserWarning[] }) {
  if (warnings.length === 0) return null;
  return (
    <ul className="border-warn/30 bg-warn-muted text-2xs text-ink space-y-1 rounded border p-2">
      {warnings.map((w, i) => (
        <li key={`${w.code}-${i}`} className="flex gap-1.5">
          <span className="text-warn">!</span>
          <span>{describeWarning(w)}</span>
        </li>
      ))}
    </ul>
  );
}

/* ── Vendor select ───────────────────────────────────────────────────────────
   `GET /vendors` caps pageSize at 500 and has no `active` filter, so inactive
   vendors are dropped here — they must not be selectable for new work, but a
   PO already pointing at one still renders its name.                         */

export function useVendorOptions() {
  return useQuery({
    queryKey: vendorKeys.list({ all: true }),
    queryFn: () =>
      api.get<PaginatedResponse<Vendor>>('/vendors', {
        params: { pageSize: 500, sort: 'name', order: 'asc' },
      }),
    staleTime: 60_000,
    select: (res) => res.data ?? [],
  });
}

export function VendorSelect({
  value,
  onChange,
  vendors,
  loading,
  includeInactive = false,
  placeholder = 'Choose a vendor…',
  size = 'md',
  className,
  id,
}: {
  value: number | null;
  onChange: (vendorId: number | null) => void;
  vendors: Vendor[] | undefined;
  loading?: boolean;
  includeInactive?: boolean;
  placeholder?: string;
  size?: 'sm' | 'md';
  className?: string;
  id?: string;
}) {
  const options = useMemo(() => {
    const list = vendors ?? [];
    const visible = includeInactive ? list : list.filter((v) => v.active !== false);
    // Keep a currently-selected inactive vendor in the list so the control
    // never silently blanks an existing selection.
    if (value != null && !visible.some((v) => v.id === value)) {
      const found = list.find((v) => v.id === value);
      if (found) return [found, ...visible];
    }
    return visible;
  }, [vendors, includeInactive, value]);

  if (loading && !vendors) return <Skeleton className={cn('h-8 w-full', className)} />;

  return (
    <Select
      id={id}
      size={size}
      className={className}
      value={value === null ? '' : String(value)}
      onChange={(e) => {
        const next = e.currentTarget.value;
        onChange(next === '' ? null : Number(next));
      }}
    >
      <option value="">{placeholder}</option>
      {options.map((v) => (
        <option key={v.id} value={v.id}>
          {v.name}
          {v.active === false ? ' (inactive)' : ''}
        </option>
      ))}
    </Select>
  );
}

/* ── Product picker ──────────────────────────────────────────────────────────
   Used by the PO builder, the quick-invoice path and the PO line editor.

   Contract note: `?vendor_id=` with ONE id does not FILTER the catalog — the
   API uses it to enrich rows with that vendor's cost and to say which products
   the vendor has a link for. So the picker labels which rows are actually
   linked rather than pretending the list is vendor-scoped.

   Order (`rank=exact_first`, FR-PICK): an exact barcode, case barcode or the
   vendor's own SKU first, then names starting with the text, then names
   containing it, each by name. It is typed from the keyboard: arrows move,
   Enter picks, and focus comes back to the search box after every pick.      */

export type PickedProduct = {
  id: number;
  name: string;
  price: number;
  cost: number | null;
  vendorUnitCost: number | null;
  vendorCaseCost: number | null;
  vendorUnitsPerCase: number | null;
  vendorSku: string | null;
  linkedToVendor: boolean;
  /** Derived from `soldByWeight` — `GET /products` does not return `unit`. */
  unit: 'ea' | 'lb';
  stock: number | null;
};

export function toPicked(row: ProductSummary): PickedProduct {
  return {
    id: Number(row.id),
    name: row.name,
    price: row.price,
    cost: row.cost ?? null,
    vendorUnitCost: row.vendorUnitCost ?? null,
    vendorCaseCost: row.vendorCaseCost ?? null,
    vendorUnitsPerCase: row.vendorUnitsPerCase ?? null,
    vendorSku: row.vendorSku ?? null,
    // Any link of the vendor counts, preferred or not, with or without a cost
    // or SKU. An API that does not send the flag falls back to what a link
    // usually carries.
    linkedToVendor:
      row.vendorLinked ?? (row.vendorUnitCost != null || row.vendorSku != null),
    unit: row.soldByWeight ? 'lb' : 'ea',
    stock: row.stock ?? null,
  };
}

/** The rows the picker shows, and how many products matched in all. */
export type ProductSearchResult = { rows: PickedProduct[]; total: number };

export const PICKER_PAGE_SIZE = 25;

export function useProductSearch(search: string, vendorId: number | null) {
  const term = search.trim();
  return useQuery({
    queryKey: ['products', 'picker', term, vendorId],
    queryFn: () =>
      api.get<PaginatedResponse<ProductSummary>>('/products', {
        params: {
          search: term,
          pageSize: PICKER_PAGE_SIZE,
          active: true,
          rank: 'exact_first',
          ...(vendorId ? { vendor_id: vendorId } : {}),
        },
      }),
    enabled: term.length >= 2,
    staleTime: 30_000,
    select: (res): ProductSearchResult => {
      const rows = (res.data ?? []).map(toPicked);
      return { rows, total: Math.max(res.total ?? rows.length, rows.length) };
    },
  });
}

/** The "more results" line under a cut list, or null when everything is shown. */
export function moreResultsHint(shown: number, total: number): string | null {
  return total > shown
    ? `Showing the first ${num(shown)} of ${num(total)}. Type more to narrow the list.`
    : null;
}

export function ProductPicker({
  vendorId,
  onPick,
  placeholder = 'Search products by name, SKU or barcode…',
  autoFocus,
  disabledIds,
}: {
  vendorId: number | null;
  onPick: (product: PickedProduct) => void;
  placeholder?: string;
  autoFocus?: boolean;
  /** Already on the order — shown, but marked and inert. */
  disabledIds?: Set<number>;
}) {
  const [term, setTerm] = useState('');
  const [debounced, setDebounced] = useState('');
  const [open, setOpen] = useState(false);
  const [activeId, setActiveId] = useState<number | null>(null);
  // Enter pressed before the results for what is typed have arrived: pick the
  // highlighted row as soon as they do (a scanner sends its Enter at once).
  const [pendingEnter, setPendingEnter] = useState<string | null>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listId = useId();

  useEffect(() => {
    const t = setTimeout(() => setDebounced(term), 180);
    return () => clearTimeout(t);
  }, [term]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!boxRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const query = useProductSearch(debounced, vendorId);
  const rows = useMemo(() => query.data?.rows ?? [], [query.data]);
  const total = query.data?.total ?? 0;
  const showList = open && debounced.trim().length >= 2;

  const enabled = (p: PickedProduct) => !(disabledIds?.has(p.id) ?? false);
  // The highlighted row: the one the arrows or the mouse chose, else the first
  // row even when it is already on the order, so Enter on an already-added
  // exact match does nothing rather than adding the next, different product.
  const highlighted =
    rows.find((p) => p.id === activeId && enabled(p)) ?? rows[0] ?? null;

  useEffect(() => {
    if (highlighted === null) return;
    document
      .getElementById(`${listId}-${highlighted.id}`)
      ?.scrollIntoView?.({ block: 'nearest' });
  }, [highlighted, listId]);

  const pick = useCallback(
    (product: PickedProduct) => {
      onPick(product);
      setTerm('');
      setDebounced('');
      setOpen(false);
      setActiveId(null);
      setPendingEnter(null);
      inputRef.current?.focus();
    },
    [onPick]
  );

  // An Enter that arrived early picks once the results for that text are in.
  useEffect(() => {
    if (pendingEnter === null) return;
    if (debounced.trim() !== pendingEnter || !query.isSuccess) return;
    setPendingEnter(null);
    // A scanner's early Enter picks only when the code found one product. With
    // several rows we cannot tell the exact code from a prefix or name match,
    // so the list stays open for the operator to choose.
    const only = rows.length === 1 ? rows[0]! : null;
    if (only && enabled(only)) pick(only);
    // `enabled` closes over `disabledIds`, which is read fresh each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingEnter, debounced, query.isSuccess, rows, pick]);

  const move = (step: 1 | -1) => {
    const pickable = rows.filter(enabled);
    if (pickable.length === 0) return;
    const at = pickable.findIndex((p) => p.id === highlighted?.id);
    const next = pickable[(at + step + pickable.length) % pickable.length]!;
    setActiveId(next.id);
  };

  return (
    <div ref={boxRef} className="relative">
      <Input
        ref={inputRef}
        value={term}
        autoFocus={autoFocus}
        placeholder={placeholder}
        prefix={<Search />}
        role="combobox"
        aria-label="Search products"
        aria-expanded={showList}
        aria-controls={showList ? listId : undefined}
        aria-activedescendant={
          showList && highlighted ? `${listId}-${highlighted.id}` : undefined
        }
        autoComplete="off"
        onChange={(e) => {
          setTerm(e.currentTarget.value);
          setPendingEnter(null);
          setActiveId(null);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        // The list's rows never take focus, so a blur is the operator leaving.
        onBlur={() => {
          setOpen(false);
          setPendingEnter(null);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            setOpen(false);
            setPendingEnter(null);
          } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault();
            setOpen(true);
            move(e.key === 'ArrowDown' ? 1 : -1);
          } else if (e.key === 'Enter') {
            // Never submit a surrounding form from the search box.
            e.preventDefault();
            const text = term.trim();
            if (text.length < 2) return;
            if (debounced.trim() === text && query.isSuccess) {
              if (highlighted && enabled(highlighted)) pick(highlighted);
            } else {
              setDebounced(term);
              setPendingEnter(text);
            }
          }
        }}
      />
      {showList ? (
        <div className="border-line-strong bg-surface-raised absolute inset-x-0 top-full z-30 mt-1 overflow-hidden rounded border shadow-lg shadow-black/25">
          <div
            id={listId}
            role="listbox"
            aria-label="Matching products"
            className="max-h-64 overflow-y-auto"
          >
            {query.isPending ? (
              <div className="space-y-1.5 p-2">
                <Skeleton className="h-3 w-2/3" />
                <Skeleton className="h-3 w-1/2" />
                <Skeleton className="h-3 w-3/4" />
              </div>
            ) : query.isError ? (
              <div className="text-2xs text-negative p-3">Product search failed.</div>
            ) : rows.length === 0 ? (
              <div className="text-2xs text-ink-faint p-3">
                No product matches “{debounced}”.
              </div>
            ) : (
              rows.map((p) => {
                const already = !enabled(p);
                const active = highlighted?.id === p.id;
                return (
                  <button
                    key={p.id}
                    id={`${listId}-${p.id}`}
                    type="button"
                    role="option"
                    aria-selected={active}
                    aria-disabled={already || undefined}
                    tabIndex={-1}
                    disabled={already}
                    // Keep the search box focused through a click.
                    onMouseDown={(e) => e.preventDefault()}
                    onMouseEnter={() => {
                      if (!already) setActiveId(p.id);
                    }}
                    onClick={() => pick(p)}
                    className={cn(
                      'border-line-soft flex w-full items-center gap-2 border-b px-2.5 py-1.5 text-left last:border-0',
                      already
                        ? 'cursor-not-allowed opacity-45'
                        : active
                          ? 'bg-surface-hover'
                          : 'hover:bg-surface-hover'
                    )}
                  >
                    <span className="text-ink min-w-0 flex-1 truncate text-xs">
                      {p.name}
                    </span>
                    {p.unit === 'lb' ? <Badge tone="info">lb</Badge> : null}
                    {p.linkedToVendor ? (
                      <Badge tone="signal">
                        <Check className="size-2.5" /> linked
                      </Badge>
                    ) : null}
                    {already ? <Badge tone="neutral">already added</Badge> : null}
                    <span className="text-2xs text-ink-muted w-16 shrink-0 text-right font-mono tabular-nums">
                      {money(p.vendorUnitCost ?? p.cost)}
                    </span>
                  </button>
                );
              })
            )}
          </div>
          {/* Outside the scrolling list, so a cut list always says so. */}
          {rows.length > 0 && moreResultsHint(rows.length, total) ? (
            <div
              data-testid="picker-more"
              className="text-2xs text-ink-faint border-line-soft bg-surface-sunken border-t px-2.5 py-1.5"
            >
              {moreResultsHint(rows.length, total)}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/* ── Small readouts ──────────────────────────────────────────────────────── */

/** A cost delta with its sign carried by colour. Up is bad when buying. */
export function CostVariance({
  from,
  to,
  className,
}: {
  from: number | null | undefined;
  to: number | null | undefined;
  className?: string;
}) {
  if (from === null || from === undefined || to === null || to === undefined) {
    return <span className={cn('text-2xs text-ink-faint', className)}>—</span>;
  }
  const delta = to - from;
  if (delta === 0) {
    return <span className={cn('text-2xs text-ink-faint', className)}>no change</span>;
  }
  const pct = from > 0 ? (delta / from) * 100 : null;
  return (
    <span
      className={cn(
        'text-2xs font-mono tabular-nums',
        delta > 0 ? 'text-negative' : 'text-positive',
        className
      )}
    >
      {delta > 0 ? '+' : '−'}
      {money(Math.abs(delta)).replace('$', '$')}
      {pct === null ? '' : ` (${delta > 0 ? '+' : '−'}${Math.abs(pct).toFixed(1)}%)`}
    </span>
  );
}

export const IMPORT_STATUS_FILTERS: Array<{
  value: ImportStatus | 'all';
  label: string;
}> = [
  { value: 'all', label: 'All' },
  { value: 'matched', label: 'Ready' },
  { value: 'queued', label: 'Queued' },
  { value: 'parsing', label: 'Parsing' },
  { value: 'failed', label: 'Failed' },
  { value: 'confirmed', label: 'Imported' },
];

export const PO_STATUS_FILTERS: Array<{ value: PoStatus | 'all'; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'draft', label: 'Draft' },
  { value: 'submitted', label: 'Ordered' },
  { value: 'partially_received', label: 'Partial' },
  { value: 'received', label: 'Received' },
  { value: 'cancelled', label: 'Cancelled' },
];
